// services/DoctorService.ts
// //doctorTester Services


import axios from 'axios';
import { baseUrl } from '../utils/config';
import { AuthService } from './AuthService';
import { ReactNode } from 'react';

export type CreateDoctorPayload = {
    nom: string;
    prenom: string;
    email: string;
    mot_de_passe: string;
    pays: string;
    role?: string; // Optional, can be set directly in the service
};

export type CreateProfessionalPayload = {
    utilisateur_id: number;
    type_profil: 'Coach' | 'Médecin'; // Assuming only two types, extend if needed
    specialites: string[];
    sous_specialites: string;
    numero_ordre_medecins: string;
    date_naissance: string; // ISO format 'YYYY-MM-DD'
    pays: string;
    telephone: string;
    honoraire_montant: number;
    honoraire_devise: string;
    rib: string;
    banque: string;
    agence: string;
    code_swift: string;
    langues: string[];
    pays_exercice: string[];
    info_supplementaires: string;
};

export interface Doctor {
    role: ReactNode;
    activite: any;
    utilisateur_id: number;
    nom: string;
    prenom: string; 
    email: string;
    mot_de_passe: string;   
    pays: string;
    adresse_pays: string;
    email_contact: string;
    id: number;
}

export const DoctorService = {
    // Create axios instance with authentication interceptor
    api: (() => {
        const instance = axios.create({ baseURL: baseUrl });
        instance.interceptors.request.use((config) => {
            const token = localStorage.getItem('auth_token');
            if (token) {
                config.headers = config.headers || {};
                (config.headers as any).Authorization = `Bearer ${token}`;
            }
            return config;
        });
        return instance;
    })(),

    async getDoctorDetailsByMail(email: string): Promise<any> {
        try {
            const response = await this.api.get<any>(`/doctors/details/by-email/${email}`);
            return response.data;
        } catch (error: unknown) {
            console.error('Erreur lors de la récupération du docteur:', error);
            throw error;
        }
    },

async createDoctor(data: CreateDoctorPayload): Promise<void> {
  const userPayload = {
    nom: data.nom,
    prenom: data.prenom,
    email: data.email,
    mot_de_passe: data.mot_de_passe,
    pays: data.pays,
    role: 'doctorTester', // Set role directly during user creation
  };
  console.log('User payload:', userPayload);

  let utilisateur_id: number | undefined;

  try {
    const userRes = await this.api.post(`/api/utilisateurs/`, userPayload);
    utilisateur_id = userRes.data.id;

    if (!utilisateur_id) {
      throw new Error('Utilisateur ID not returned from user creation.');
    }

    console.log('Doctor created successfully with ID:', utilisateur_id);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail || error.message;
      console.error('Create doctor error:', {
        detail,
        status: error.response?.status,
        data: error.response?.data,
      });
      if (utilisateur_id) {
        try {
          await this.api.delete(`/api/utilisateurs/${utilisateur_id}/`, { timeout: 5000 });
          console.log('User deleted due to creation failure');
        } catch (deleteError) {
          console.error('Failed to delete user:', deleteError);
        }
      }
      throw detail;
    }
    console.error('Network or unexpected error:', {
      message: (error as Error).message,
      code: (error as any).code,
      stack: (error as Error).stack,
    });
    throw new Error('Network error or unexpected issue creating doctor');
  }
},
async deleteDoctorUser(doctorId: number, userId: number): Promise<void> {
  try {
    await deleteDoctor(doctorId); // Call directly as a function
    await AuthService.deleteUser(userId);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail || error.message;
      console.error('Error deleting doctor user:', {
        detail,
        status: error.response?.status,
        data: error.response?.data,
      });
    } else {
      console.error('Unexpected error deleting doctor user:', error);
    }
    throw new Error('Failed to delete doctor user');
  }
},

async deleteDoctor(doctorId: number): Promise<void> {
  try {
    console.log('Deleting doctor with ID:', doctorId, 'at endpoint:', `${baseUrl}/partenaires/${doctorId}`);
    await axios.delete(`${baseUrl}/partenaires/${doctorId}`, {
      timeout: 5000, // 5-second timeout
    });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const detail = error.response?.data?.detail || error.message;
      console.error('Error deleting doctor:', {
        detail,
        status: error.response?.status,
        data: error.response?.data,
      });
      if (error.response?.status === 404) {
        console.warn('Doctor not found, skipping deletion');
      }
    } else {
      console.error('Unexpected error deleting doctor:', error);
    }
    throw new Error('Failed to delete doctor');
  }
},


    async createProfetional(data: CreateDoctorPayload): Promise<void> {
        // 1. Extract user-related fields
        const userPayload = {
            nom: data.nom,
            prenom: data.prenom,
            email: data.email,
            mot_de_passe: data.mot_de_passe,
        };

        try {
            const userRes = await this.api.post(`/api/utilisateurs/`, userPayload);
            const utilisateur_id = userRes.data.id;

            if (!utilisateur_id) {
                throw new Error('Utilisateur ID not returned from user creation response.');
            }

            // 2. Compose partner creation payload
            const doctorPayload = {
                utilisateur_id,
                nom: data.nom,
                prenom: data.prenom,
                email: data.email,
                mot_de_passe: data.mot_de_passe,
                pays: data.pays,

               
                
            };

            // 3. Send second request to create the doctor
            await this.api.post(`${baseUrl}/docteurs/`, doctorPayload);
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const detail = error.response?.data?.detail;
                throw detail;
            }

        }
    },

    async getAllDoctors({
  search = '',
  skip = 0,
  limit = 10,
}: {
  search: string;
  skip?: number;
  limit?: number;
}): Promise<Doctor[]> {
  try {
    const response = await axios.post(`${baseUrl}/users/by-role?skip=${skip}&limit=${limit}`, {
      role:'doctorTester', // Default to doctorTester if no role specified
      search,
    });
    return response.data;
  } catch (error: any) {
    if (error.response) {
      throw new Error(error.response.data.detail || 'Failed to fetch doctors');
    } else if (error.request) {
      throw new Error('No response from server');
    } else {
      throw new Error('Request error: ' + error.message);
    }
  }
},

    // Utility to extract doctor_id from userInfo 
    getDoctorIdFromUserInfo(userInfo: any): number | undefined {
        if (!userInfo) return undefined;
        // If userInfo has a nested doctor object
        if (userInfo.docteur && userInfo.docteur.id) {
            return userInfo.docteur.id;
        }
        // If userInfo is flat (legacy)
        if (userInfo.id) {
            return userInfo.id;
        }
        return undefined;
    },

    async createProductByDoctorId(product: {
        name: string;
        price: number | string;
        description: string;
        category: 'Sport' | 'Beauty' | 'OTC';
        inStock: boolean;
        stock_number: number;
    }): Promise<void> {
        try {
            // Get doctor_id from localStorage
            const userInfoRaw = localStorage.getItem("userInfo");
            let userInfo;
            try {
                userInfo = JSON.parse(userInfoRaw || '""');
            } catch (e) {
                console.error('Error parsing userInfo from localStorage:', userInfoRaw, e);
                throw new Error('Invalid userInfo in localStorage');
            }
            console.log('userInfo from localStorage:', userInfo);
            const doctor_id = this.getDoctorIdFromUserInfo(userInfo);
            if (!doctor_id) {
                console.error('No doctor_id found in userInfo:', userInfo);
                throw new Error('No doctor_id found');
            }
            // Map category string to id
            let category_id = '';
            if (product.category === 'Sport') category_id = '1';
            else if (product.category === 'Beauty') category_id = '2';
            else if (product.category === 'OTC') category_id = '3';
            else {
                console.error('Invalid category:', product.category);
                throw new Error('Invalid category');
            }
            // Generate product id
            const now = new Date();
            const id = `prod_${now.getTime()}`;
            // Prepare payload
            const payload = {
                name: product.name,
                price: product.price,
                id,
                description: product.description,
                category_id,
                imageData: null,
                imageMimeType: null,
                inStock: product.inStock,
                doctor_id,
                stock_number: product.stock_number
            };
            console.log('Incoming product argument:', product);
            console.log('Creating product with payload:', payload);
            await this.api.post(`${baseUrl}/products`, payload);
            console.log('Product created successfully');
        } catch (error) {
            console.error('Error creating product:', error);
            throw error;
        }
    },

    async getProductByDoctor(doctor_id: number): Promise<any[]> {
        try {
            const response = await this.api.get(`${baseUrl}/products/doctor/${doctor_id}/`);
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des produits du docteur:', error);
            throw error;
        }
    },

    async deleteProductById(product_id: string | number): Promise<void> {
        try {
            await this.api.delete(`${baseUrl}/products/${product_id}`);
        } catch (error) {
            console.error('Erreur lors de la suppression du produit:', error);
            throw error;
        }
    },

    async updateProductByPartnerId(product: {
        id: number | string;
        name: string;
        price: number | string;
        description: string;
        category: 'Sport' | 'Beauty' | 'OTC';
        inStock: boolean;
        stock_number: number;
        imageData?: string | null;
        imageMimeType?: string | null;
    }): Promise<void> {
        try {
            // Get partner_id from localStorage
            const userInfoRaw = localStorage.getItem("userInfo");
            let userInfo;
            try {
                userInfo = JSON.parse(userInfoRaw || '""');
            } catch (e) {
                console.error('Error parsing userInfo from localStorage:', userInfoRaw, e);
                throw new Error('Invalid userInfo in localStorage');
            }
            const doctor_id = this.getDoctorIdFromUserInfo(userInfo);
            if (!doctor_id) {
                console.error('No doctor_id found in userInfo:', userInfo);
                throw new Error('No doctor_id found');
            }
            // Map category string to id
            let category_id = '';
            if (product.category === 'Sport') category_id = '1';
            else if (product.category === 'Beauty') category_id = '2';
            else if (product.category === 'OTC') category_id = '3';
            else {
                console.error('Invalid category:', product.category);
                throw new Error('Invalid category');
            }
            // Prepare payload for update
            const payload = {
                id: product.id,
                name: product.name,
                price: product.price,
                description: product.description,
                category_id,
                imageData: product.imageData || null,
                imageMimeType: product.imageMimeType || null,
                inStock: product.inStock,
                doctor_id,
                stock_number: product.stock_number
            };
            console.log('Updating product with payload:', payload);
           
            await this.api.post(`${baseUrl}/products`, payload);
            console.log('Product updated successfully');
        } catch (error) {
            console.error('Error updating product:', error);
            throw error;
        }
    },
};
function deleteDoctor(doctorId: number) {
    throw new Error('Function not implemented.');
}

